import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Share2 } from "lucide-react";
import { SOULMARK } from "@/constants/testIds";

/**
 * ShareSoulMark — client-side canvas share card for the Soul Mark reveal.
 *
 * Renders two controls:
 *   ▸ "Save my Soul Mark"  — primary
 *   ▸ "Share"              — quieter, below
 *
 * Both trigger the same pipeline: build a 1080×1350 portrait PNG on a
 * canvas, then either invoke navigator.share({ files: [png] }) when
 * supported, or fall back to a direct download. Every entry point is
 * wrapped in try/catch — a failed share never breaks the screen.
 */

const W = 1080;
const H = 1350;
const BG = "#0B0E1A";
const GOLD = "#E8D3A2";
const PARCH = "#F4EFE6";

function truncateSentence(text, max = 90) {
  if (!text) return "";
  // Pull the most evocative single sentence: first non-empty sentence
  const first = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .find((s) => s.length > 0);
  const chosen = first || text.trim();
  if (chosen.length <= max) return chosen;
  return chosen.slice(0, max - 1).trimEnd() + "…";
}

async function loadImageAsBlob(url) {
  // Fetch first so canvas is never tainted, regardless of CORS headers
  const res = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!res.ok) throw new Error("Failed to fetch mark");
  return await res.blob();
}

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image decode failed"));
    };
    img.src = url;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

async function renderCard({ imageUrl, title, soulName, sentence }) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Ensure Cormorant Garamond is available before drawing text
  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch (_) {
    /* noop */
  }

  // Background
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // Mark artwork — centred, ~760 wide, in upper two-thirds
  if (imageUrl) {
    try {
      const blob = await loadImageAsBlob(imageUrl);
      const img = await blobToImage(blob);
      const markW = 760;
      const ratio = img.height / img.width;
      const markH = markW * ratio;
      const markX = (W - markW) / 2;
      // Upper-two-thirds anchor: mark centre around y ≈ H * 0.34
      const markCentreY = H * 0.34;
      const markY = markCentreY - markH / 2;

      // Soft golden halo
      ctx.save();
      const halo = ctx.createRadialGradient(
        W / 2,
        markCentreY,
        markW * 0.15,
        W / 2,
        markCentreY,
        markW * 0.7,
      );
      halo.addColorStop(0, "rgba(232,211,162,0.16)");
      halo.addColorStop(1, "rgba(232,211,162,0)");
      ctx.fillStyle = halo;
      ctx.fillRect(0, markY - 40, W, markH + 80);
      ctx.restore();

      ctx.drawImage(img, markX, markY, markW, markH);
    } catch (_) {
      /* mark load failed — proceed without image */
    }
  }

  // ── Text block, centred, below the mark ─────────────────────────────
  let cursorY = Math.round(H * 0.62);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  // Soul title — Cormorant Garamond italic, gold
  if (title) {
    ctx.fillStyle = GOLD;
    ctx.font = 'italic 500 62px "Cormorant Garamond", Georgia, serif';
    const titleLines = wrapText(ctx, title, W - 160);
    for (const line of titleLines) {
      ctx.fillText(line, W / 2, cursorY);
      cursorY += 72;
    }
    cursorY += 12;
  }

  // Soul name — smaller, parchment @ 60%
  if (soulName) {
    ctx.fillStyle = "rgba(244,239,230,0.60)";
    ctx.font = 'italic 400 34px "Cormorant Garamond", Georgia, serif';
    ctx.fillText(soulName, W / 2, cursorY);
    cursorY += 62;
  }

  // Sentence — parchment @ 78%, truncated
  const trimmed = truncateSentence(sentence, 90);
  if (trimmed) {
    ctx.fillStyle = "rgba(244,239,230,0.78)";
    ctx.font = 'italic 300 28px "Cormorant Garamond", Georgia, serif';
    const lines = wrapText(ctx, trimmed, W - 200);
    for (const line of lines) {
      ctx.fillText(line, W / 2, cursorY);
      cursorY += 40;
    }
  }

  // Wordmark — small letterspaced gold caps at 50%
  ctx.fillStyle = "rgba(232,211,162,0.50)";
  ctx.font = '500 20px "DM Sans", system-ui, sans-serif';
  ctx.textBaseline = "alphabetic";
  ctx.fillText("A U R A V E I L", W / 2, H - 80);

  // Hairline gold border inset 40px
  ctx.strokeStyle = "rgba(232,211,162,0.55)";
  ctx.lineWidth = 1;
  ctx.strokeRect(40.5, 40.5, W - 81, H - 81);

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas export failed"))),
      "image/png",
      1,
    );
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function ShareSoulMark({
  imageUrl,
  title,
  soulName,
  sentence,
  className = "",
}) {
  const [busy, setBusy] = useState(false);

  const filename = `auraveil-soul-mark${soulName ? "-" + soulName.replace(/\s+/g, "-").toLowerCase() : ""}.png`;

  const build = async () => {
    return await renderCard({ imageUrl, title, soulName, sentence });
  };

  const onSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await build();
      downloadBlob(blob, filename);
      toast.success("Your Soul Mark is saved.");
    } catch (_) {
      toast.error("Could not save your Soul Mark.");
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await build();
      const file = new File([blob], filename, { type: "image/png" });
      const canShareFile =
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] });
      if (canShareFile && typeof navigator.share === "function") {
        try {
          await navigator.share({
            files: [file],
            title: "My AuraVeil Soul Mark",
            text: title || "My Soul Mark",
          });
          return;
        } catch (err) {
          // User cancelled or share failed — fall through to download
          if (err && err.name === "AbortError") return;
        }
      }
      downloadBlob(blob, filename);
      toast("Share sheet unavailable — saved to your device instead.");
    } catch (_) {
      toast.error("Could not share your Soul Mark.");
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || !imageUrl;

  return (
    <div className={`flex flex-col items-start gap-3 ${className}`}>
      <button
        type="button"
        data-testid={SOULMARK.saveBtn}
        onClick={onSave}
        disabled={disabled}
        className="btn-sacred btn-sacred-solid disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        <Download size={14} strokeWidth={1.5} />
        {busy ? "Composing…" : "Save my Soul Mark"}
      </button>
      <button
        type="button"
        data-testid={SOULMARK.shareBtn}
        onClick={onShare}
        disabled={disabled}
        className="font-overline text-[11px] tracking-[0.24em] text-[#F4EFE6]/55 hover:text-[#C8A96E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        <Share2 size={12} strokeWidth={1.5} />
        Share
      </button>
    </div>
  );
}
